// src/hr/portal/SettingsLists.js -- the Settings page's time-off types and company holidays.
//   Types: locked types (Sick & Safe, protected leave) can only change color; HR's own types can be
//   renamed, change notes / carry-over and be archived (never deleted). New types are always balance
//   types people can request.
//   Holidays: skipped when counting days off and shown on Who's Out; add a date + name, or remove one.
import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { FiAlertCircle, FiCalendar, FiCheck, FiEdit2, FiLock, FiPlus, FiTrash2 } from 'react-icons/fi';
import { hr, errorText } from '../api';
import { Btn, Card, EmptyState, Field, Pill, Skeleton } from '../kit';
import { plainId } from '../kit/util';
import { typeLabel } from '../timeoff/labels';
import { shortDate, weekdayShort } from '../timeoff/dates';
import { FormError } from './FormError';

// The type palette (spec §9 type and status colors, plus a few more that read well on the dark glass).
const SWATCHES = [
  { value: '#818cf8', label: 'Indigo' },
  { value: '#38bdf8', label: 'Sky' },
  { value: '#f472b6', label: 'Pink' },
  { value: '#34d399', label: 'Green' },
  { value: '#fbbf24', label: 'Amber' },
  { value: '#f87171', label: 'Red' },
  { value: '#a78bfa', label: 'Violet' },
  { value: '#2dd4bf', label: 'Teal' },
  { value: '#fb923c', label: 'Orange' },
  { value: '#94a3b8', label: 'Slate' }
];

// A color radio group: one tab stop, arrow keys move. A stored color outside the palette is offered first.
function Swatches({ value, onChange, label }) {
  const refs = useRef([]);
  const options = SWATCHES.some((s) => s.value.toLowerCase() === String(value || '').toLowerCase())
    ? SWATCHES
    : [{ value, label: 'Current color' }, ...SWATCHES];
  const selected = Math.max(0, options.findIndex((o) => o.value.toLowerCase() === String(value || '').toLowerCase()));
  const choose = (i) => {
    const next = (i + options.length) % options.length;
    onChange(options[next].value);
    if (refs.current[next]) refs.current[next].focus();
  };
  const onKeyDown = (e, i) => {
    const moves = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
    if (moves[e.key] !== undefined) { e.preventDefault(); choose(i + moves[e.key]); }
    else if (e.key === 'Home') { e.preventDefault(); choose(0); }
    else if (e.key === 'End') { e.preventDefault(); choose(options.length - 1); }
  };
  return (
    <div className="hrp-sw" role="radiogroup" aria-label={label}>
      {options.map((o, i) => (
        <button
          key={o.value}
          ref={(el) => { refs.current[i] = el; }}
          type="button"
          role="radio"
          aria-checked={i === selected}
          aria-label={o.label}
          title={o.label}
          tabIndex={i === selected ? 0 : -1}
          className="hrp-sw__opt"
          style={{ '--hrp-sw': o.value }}
          onClick={() => choose(i)}
          onKeyDown={(e) => onKeyDown(e, i)}
        >
          {i === selected && <FiCheck aria-hidden="true" />}
        </button>
      ))}
    </div>
  );
}

function typeDescription(t) {
  if (t.isSick) return 'Never stores a reason · at least 40 hours a year';
  if (t.protected) return 'Never deducts · shows as “Out” · HR only';
  return `${t.allowNotes ? 'Notes allowed' : 'No notes'} · ${t.carriesOver ? 'carries over' : 'no carryover'}`;
}

function Check({ checked, onChange, label, hint }) {
  return (
    <label className="hrp-check hrp-check--sm">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span>
        <b>{label}</b>
        {hint && <em>{hint}</em>}
      </span>
    </label>
  );
}

// Edit form for one type. Locked types only get the color.
function TypeEditor({ type, onCancel, onSaved }) {
  const [form, setForm] = useState({ name: type.name, color: type.color, allowNotes: type.allowNotes, carriesOver: type.carriesOver });
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState('');
  const set = (key) => (value) => { setForm((f) => ({ ...f, [key]: value })); setError(''); };
  const name = typeLabel(type);

  const body = {};
  if (form.color !== type.color) body.color = form.color;
  if (!type.locked) {
    if (form.name.trim() !== type.name) body.name = form.name.trim();
    if (form.allowNotes !== type.allowNotes) body.allowNotes = form.allowNotes;
    if (form.carriesOver !== type.carriesOver) body.carriesOver = form.carriesOver;
  }
  const changed = Object.keys(body).length > 0;

  const send = async (patch, kind, text) => {
    setBusy(kind);
    setError('');
    try {
      await hr.admin.types.update(type.id, patch);
      await onSaved(text);
    } catch (err) {
      setError(errorText(err, "Couldn't save the type. Try again."));
      setBusy(null);
    }
  };

  return (
    <form className="hrp-ty__edit" onSubmit={(e) => { e.preventDefault(); if (changed) send(body, 'save', `${name} saved`); }} noValidate>
      {type.locked ? (
        <p className="hrp-ty__locknote"><FiLock aria-hidden="true" />Set by New Jersey law — only the color can change.</p>
      ) : (
        <Field label="Name" value={form.name} onChange={(e) => set('name')(e.target.value)} maxLength={40} />
      )}
      <div className="hrp-field">
        <span className="hrp-field__label">Color</span>
        <Swatches label={`${name} color`} value={form.color} onChange={set('color')} />
      </div>
      {!type.locked && (
        <div className="hrp-ty__checks">
          <Check checked={form.allowNotes} onChange={set('allowNotes')} label="Notes allowed" />
          <Check checked={form.carriesOver} onChange={set('carriesOver')} label="Carries over" hint="Leftover days roll into next year" />
        </div>
      )}
      <FormError>{error}</FormError>
      <div className="hrp-ty__btns">
        {!type.locked && (
          <Btn variant="danger" size="sm" className="hrp-ty__arch" loading={busy === 'archive'} disabled={!!busy}
            onClick={() => send({ archived: true }, 'archive', `${name} archived`)}>Archive</Btn>
        )}
        <Btn variant="ghost" size="sm" onClick={onCancel} disabled={!!busy}>Cancel</Btn>
        <Btn type="submit" size="sm" icon={FiCheck} loading={busy === 'save'} disabled={!changed || !!busy}>Save</Btn>
      </div>
    </form>
  );
}

function TypeRow({ type, editing, onEdit, onCancel, onSaved }) {
  const name = typeLabel(type);
  return (
    <li className="hrp-ty__it" data-editing={editing ? 'true' : undefined}>
      <div className="hrp-ty__row">
        <Pill color={type.color} dot>{name}</Pill>
        <span className="hrp-ty__desc">{typeDescription(type)}</span>
        {type.locked && <Pill tone="mute" icon={<FiLock />} className="hrp-ty__lock">Locked</Pill>}
        {!editing && (
          <button type="button" className="hrp-iconbtn hrp-ty__ed" aria-label={type.locked ? `Change the color of ${name}` : `Edit ${name}`} onClick={onEdit}>
            <FiEdit2 aria-hidden="true" />
          </button>
        )}
      </div>
      {editing && <TypeEditor type={type} onCancel={onCancel} onSaved={onSaved} />}
    </li>
  );
}

function AddTypeForm({ types, onCancel, onSaved }) {
  const used = new Set((types || []).map((t) => String(t.color).toLowerCase()));
  const firstFree = (SWATCHES.find((s) => !used.has(s.value)) || SWATCHES[0]).value;
  const [form, setForm] = useState({ name: '', color: firstFree, allowNotes: true, carriesOver: false });
  const [nameError, setNameError] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const set = (key) => (value) => { setForm((f) => ({ ...f, [key]: value })); setError(''); if (key === 'name') setNameError(''); };

  const submit = async (e) => {
    e.preventDefault();
    const name = form.name.trim();
    if (!name) { setNameError('Enter a type name'); return; }
    setBusy(true);
    setError('');
    try {
      await hr.admin.types.create({ name, color: form.color, allowNotes: form.allowNotes, carriesOver: form.carriesOver });
      await onSaved(`${name} added`);
    } catch (err) {
      setError(errorText(err, "Couldn't add the type. Try again."));
      setBusy(false);
    }
  };

  return (
    <form className="hrp-ty__edit hrp-ty__add" onSubmit={submit} noValidate aria-label="Add a time-off type">
      <Field label="Name" value={form.name} onChange={(e) => set('name')(e.target.value)} error={nameError} maxLength={40} placeholder="e.g. Floating holiday" />
      <div className="hrp-field">
        <span className="hrp-field__label">Color</span>
        <Swatches label="New type color" value={form.color} onChange={set('color')} />
      </div>
      <div className="hrp-ty__checks">
        <Check checked={form.allowNotes} onChange={set('allowNotes')} label="Notes allowed" />
        <Check checked={form.carriesOver} onChange={set('carriesOver')} label="Carries over" hint="Leftover days roll into next year" />
      </div>
      <p className="hrp-ty__hint">People can request it like PTO. Custom types are never sick or protected leave.</p>
      <FormError>{error}</FormError>
      <div className="hrp-ty__btns">
        <Btn variant="ghost" size="sm" onClick={onCancel} disabled={busy}>Cancel</Btn>
        <Btn type="submit" size="sm" icon={FiPlus} loading={busy}>Add type</Btn>
      </div>
    </form>
  );
}

export function TypesCard({ types, loading, context, onToast }) {
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState('');
  const sorted = useMemo(() => [...(types || [])].sort((a, b) => (Number(a.sortOrder) || 0) - (Number(b.sortOrder) || 0)), [types]);
  const balance = sorted.filter((t) => !t.archived && !t.protected);
  const protectedTypes = sorted.filter((t) => !t.archived && t.protected);
  const archived = sorted.filter((t) => t.archived);

  const saved = async (text) => {
    await context.reload();
    setEditing(null);
    setAdding(false);
    onToast(text);
  };

  const restore = async (t) => {
    setBusy(t.id);
    setError('');
    try {
      await hr.admin.types.update(t.id, { archived: false });
      await context.reload();
      onToast(`${t.name} restored`);
    } catch (err) {
      setError(errorText(err, "Couldn't restore the type. Try again."));
    } finally {
      setBusy(null);
    }
  };

  const row = (t) => (
    <TypeRow
      key={t.id}
      type={t}
      editing={editing === t.id}
      onEdit={() => { setEditing(t.id); setAdding(false); }}
      onCancel={() => setEditing(null)}
      onSaved={saved}
    />
  );

  return (
    <Card
      delay={3}
      as="section"
      aria-label="Time-off types"
      title="Time-off types"
      className="hrp-ty"
      action={!adding && (
        <button type="button" className="hrp-link" onClick={() => { setAdding(true); setEditing(null); }}>
          <FiPlus aria-hidden="true" /> Add type
        </button>
      )}
    >
      {loading && sorted.length === 0 ? <Skeleton label="Loading time-off types" lines={4} height={14} /> : (
        <>
          <ul className="hrp-ty__list" aria-label="Balance types">{balance.map(row)}</ul>
          {adding && <AddTypeForm types={sorted} onCancel={() => setAdding(false)} onSaved={saved} />}
          {protectedTypes.length > 0 && (
            <>
              <div className="hrp-group hrp-ty__grp">Protected leave</div>
              <ul className="hrp-ty__list" aria-label="Protected leave">{protectedTypes.map(row)}</ul>
            </>
          )}
          {archived.length > 0 && (
            <>
              <div className="hrp-group hrp-ty__grp">Archived</div>
              <ul className="hrp-ty__list" aria-label="Archived types">
                {archived.map((t) => (
                  <li key={t.id} className="hrp-ty__it">
                    <div className="hrp-ty__row">
                      <Pill color={t.color} dot className="hrp-ty__off">{t.name}</Pill>
                      <span className="hrp-ty__desc">Kept with its history; not offered for new time off</span>
                      <Btn variant="ghost" size="sm" loading={busy === t.id} onClick={() => restore(t)} aria-label={`Restore ${t.name}`}>Restore</Btn>
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}
          {error && <p className="hrp-al__err" role="alert"><FiAlertCircle aria-hidden="true" />{error}</p>}
        </>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------------------------
// Company holidays
// ---------------------------------------------------------------------------------------------

function HolidayRow({ holiday, onRemoved }) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const keepRef = useRef(null);
  useEffect(() => { if (confirming && keepRef.current) keepRef.current.focus(); }, [confirming]);

  const remove = async () => {
    setBusy(true);
    setError('');
    try {
      await hr.admin.holidays.remove(holiday.date);
      await onRemoved(holiday);
    } catch (err) {
      setBusy(false);
      setError(errorText(err, "Couldn't remove the holiday. Try again."));
    }
  };

  return (
    <li className="hrp-hol__it">
      <div className="hrp-hol__row">
        <span className="hrp-hol__cal" aria-hidden="true">
          <b>{shortDate(holiday.date).split(' ')[0].toUpperCase()}</b>
          <span>{shortDate(holiday.date).split(' ')[1]}</span>
        </span>
        <div className="hrp-hol__txt">
          <b>{holiday.name}</b>
          <span>{`${weekdayShort(holiday.date)}, ${shortDate(holiday.date)}, ${holiday.date.slice(0, 4)}`}</span>
        </div>
        {!confirming && (
          <button type="button" className="hrp-iconbtn hrp-hol__rm" aria-label={`Remove ${holiday.name} (${holiday.date})`} onClick={() => setConfirming(true)}>
            <FiTrash2 aria-hidden="true" />
          </button>
        )}
      </div>
      {confirming && (
        <div className="hrp-tl__confirm" role="group" aria-label={`Remove ${holiday.name}?`}>
          <span>{`Remove ${holiday.name}? Day counts that include it go up by a day.`}</span>
          <span className="hrp-tl__btns">
            <Btn ref={keepRef} variant="ghost" size="sm" onClick={() => setConfirming(false)} disabled={busy}>Keep</Btn>
            <Btn variant="danger" size="sm" icon={FiTrash2} loading={busy} onClick={remove}>Remove</Btn>
          </span>
        </div>
      )}
      {error && <p className="hrp-al__err" role="alert"><FiAlertCircle aria-hidden="true" />{error}</p>}
    </li>
  );
}

export function HolidaysCard({ holidays, loading, yearRange, context, onToast }) {
  const ids = `hrp-hol-${plainId(useId())}`;
  const [showPast, setShowPast] = useState(false);
  const [form, setForm] = useState({ date: '', name: '' });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const sorted = useMemo(() => [...(holidays || [])].sort((a, b) => a.date.localeCompare(b.date)), [holidays]);
  const from = (yearRange && yearRange.start) || '';
  const upcoming = sorted.filter((h) => h.date >= from);
  const past = sorted.filter((h) => h.date < from);
  const shown = showPast ? [...past, ...upcoming] : upcoming;

  const add = async (e) => {
    e.preventDefault();
    const name = form.name.trim();
    const found = {};
    if (!form.date) found.date = 'Pick a date';
    if (!name) found.name = 'Enter a holiday name';
    setErrors(found);
    if (Object.keys(found).length) return;
    setBusy(true);
    try {
      await hr.admin.holidays.add({ date: form.date, name });
      await context.reload();
      setForm({ date: '', name: '' });
      onToast(`${name} added`);
    } catch (err) {
      setErrors({ form: errorText(err, "Couldn't add the holiday. Try again.") });
    } finally {
      setBusy(false);
    }
  };

  const removed = async (holiday) => {
    await context.reload();
    onToast(`${holiday.name} removed`);
  };

  return (
    <Card delay={4} as="section" aria-label="Company holidays" title="Company holidays" className="hrp-hol"
      action={past.length > 0 && (
        <button type="button" className="hrp-link" aria-expanded={showPast} onClick={() => setShowPast((v) => !v)}>
          {showPast ? 'Hide past holidays' : `Show past (${past.length})`}
        </button>
      )}
    >
      <p className="hrp-set__lead">Skipped when counting days off, and shown on Who's Out.</p>
      {loading && sorted.length === 0 && <Skeleton label="Loading holidays" lines={3} height={14} />}
      {!loading && shown.length === 0 && (
        <EmptyState compact icon={FiCalendar} title="No holidays yet">Add the days the office is closed.</EmptyState>
      )}
      {shown.length > 0 && (
        <ul className="hrp-hol__list" aria-label="Holidays">
          {shown.map((h) => <HolidayRow key={h.date} holiday={h} onRemoved={removed} />)}
        </ul>
      )}
      <form className="hrp-hol__add" onSubmit={add} noValidate aria-labelledby={`${ids}-add`}>
        <span id={`${ids}-add`} className="hrp-sr">Add a holiday</span>
        <Field label="Date" type="date" value={form.date} error={errors.date} className="hrp-hol__date"
          onChange={(e) => { const { value } = e.target; setForm((f) => ({ ...f, date: value })); setErrors({}); }} />
        <Field label="Holiday" value={form.name} error={errors.name} maxLength={60} placeholder="e.g. Labor Day" className="hrp-hol__name"
          onChange={(e) => { const { value } = e.target; setForm((f) => ({ ...f, name: value })); setErrors({}); }} />
        <Btn type="submit" size="sm" icon={FiPlus} loading={busy} className="hrp-hol__addbtn">Add holiday</Btn>
      </form>
      <FormError>{errors.form}</FormError>
    </Card>
  );
}
